import { FieldRules, ReportFormRuleTemplate, RuleContext } from '@navstation-portal/reporting/data-model/ui';
import { evaluate } from '@navstation-portal/reporting/util/form-rules';

// Splits a field path into the table that owns the column and the column
// itself. A plain id, 'ifo', belongs to the section. A two-part id,
// 'heating.ifo', belongs to that table of the section. Nothing else is a path:
// an id with more parts names no table, so it reads as a plain id and finds
// nothing, which is what a typo should do.
export const splitFieldPath = (fieldPath: string): { tableId: string | null; fieldId: string } => {
  const parts = fieldPath.split('.');
  return parts.length === 2 ? { tableId: parts[0], fieldId: parts[1] } : { tableId: null, fieldId: fieldPath };
};

// The rules for one field. A table owns its columns, so a column reads its
// table and nothing else, and `section.fields` holds the fields that belong to
// no table. Two tables that draw the same column hold an entry each, which is
// what lets them differ.
export const findField = (
  template: ReportFormRuleTemplate | null,
  tabId: string,
  sectionId: string,
  fieldPath: string
): FieldRules | undefined => {
  const section = template?.tabs?.[tabId]?.sections?.[sectionId];
  const { tableId, fieldId } = splitFieldPath(fieldPath);
  return tableId === null ? section?.fields?.[fieldId] : section?.tables?.[tableId]?.fields?.[fieldId];
};

// True when the user can see the field: its tab shows, its section shows, the
// table that draws it shows, and the field itself shows. Each rule fails open,
// so a missing rule shows the item, the same answer the visibility methods
// give. The store and the stale-status check both use it.
export const isFieldOnScreen = (
  template: ReportFormRuleTemplate | null,
  context: RuleContext,
  tabId: string,
  sectionId: string,
  fieldPath: string
): boolean => {
  const tab = template?.tabs?.[tabId];
  const section = tab?.sections?.[sectionId];
  const { tableId } = splitFieldPath(fieldPath);
  const rules = [
    tab?.isTabVisible,
    section?.isSectionVisible,
    tableId === null ? undefined : section?.tables?.[tableId]?.isTableVisible,
    findField(template, tabId, sectionId, fieldPath)?.isFieldVisible,
  ];
  return rules.every((rule) => (rule ? evaluate(rule, context) : true));
};
